import { InputGroup, InputGroupProps } from '@pvh-components/input-group';
import React, { FocusEvent, useCallback, useRef } from 'react';
import ChainableNumberField from './ChainableNumberField';
import { BoundedDateParsingErrors } from './dateValidation';
import styles from './datePicker.module.css';
import { toKebabCase } from '../../utils/casing';
import { WithTestId } from '../../types';

export const toFieldErrors = (
  parseErrors: Array<BoundedDateParsingErrors>
): Array<DateFieldName> => {
  if (parseErrors.length > 0) {
    // Show errors for all fields
    return ['DD', 'MM', 'YYYY'];
  }
  return [];
};

export interface DateFields {
  DD: string;
  MM: string;
  YYYY: string;
}

export type DateFieldName = keyof DateFields;
type FocusEventHandler = (event: FocusEvent<HTMLInputElement>) => void;

export type DatePickerProps = Omit<InputGroupProps, 'isError' | 'children'> &
  WithTestId & {
    value: DateFields | undefined;
    onChange: (value: DateFields | undefined) => void;
    isError?: Array<DateFieldName>;
    onBlur?: (event: React.FocusEvent<Element, Element>) => void;
    required?: boolean;
    placeholders?: {
      day: string;
      month: string;
      year: string;
    };
  };

const EMPTY_FIELD_STATE: DateFields = {
  DD: '',
  MM: '',
  YYYY: '',
};

/**
 * Ensures that the 'onBlur' event for multiple inputs, is only called once.
 */
const useInputGroupBlurEvent = (
  onBlur: FocusEventHandler | undefined
): {
  handleBlur: FocusEventHandler;
  handleFocus: FocusEventHandler;
} => {
  const focusedInputName = useRef<string | undefined>();

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) =>
      (focusedInputName.current = event.target.name),
    []
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      focusedInputName.current = undefined;
      setTimeout(() => {
        if (focusedInputName.current === undefined) {
          onBlur?.(event);
        }
      }, 200);
    },
    [onBlur]
  );

  return { handleBlur, handleFocus };
};

/**
 * This is a controlled date picker component. This contains no validation other
 * than restricting the inputs to only contain numbers and have a maxLength.
 *
 * The function 'parseDateField' is offered to do easy validation of the state
 * of this component. It will return an errors list, that can be used for fine-
 * grained error
 */
const DatePicker = ({
  value = EMPTY_FIELD_STATE,
  onChange,
  isError = [],
  onBlur,
  required = false,
  placeholders = { day: 'DD', month: 'MM', year: 'YYYY' },
  dataTestIdPrefix,
  ...rest
}: DatePickerProps) => {
  const { handleBlur, handleFocus } = useInputGroupBlurEvent(onBlur);

  // @todo We are mixing controlled components and refs. This  needs to be
  // adjusted to fully use refs.
  const dayField = useRef<HTMLInputElement | undefined>();
  const monthField = useRef<HTMLInputElement | undefined>();
  const yearField = useRef<HTMLInputElement | undefined>();

  const handleBlurDay = (event: FocusEvent<HTMLInputElement>) => {
    onChange({ ...value, DD: padField(value.DD) });
    handleBlur(event);
  };

  const handleBlurMonth = (event: FocusEvent<HTMLInputElement>) => {
    onChange({ ...value, MM: padField(value.MM) });
    handleBlur(event);
  };

  const handleRightBoundDay = () => {
    monthField.current?.focus();
    monthField.current?.select();
  };
  const handleRightBoundMonth = () => {
    yearField.current?.focus();
    yearField.current?.select();
  };

  return (
    <InputGroup isError={isError.length > 0} {...rest}>
      <div className={styles.datePickerLayout}>
        <ChainableNumberField
          onRightBound={handleRightBoundDay}
          label={placeholders.day}
          name="day"
          required={required}
          maxLength={2}
          isError={isError.includes('DD')}
          value={value.DD}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, DD: event.target.value })
          }
          onBlur={handleBlurDay}
          onFocus={handleFocus}
          ref={dayField}
          className={styles.datePickerField}
          dataTestIdPrefix={toKebabCase(dataTestIdPrefix, 'day')}
        />
        <ChainableNumberField
          onRightBound={handleRightBoundMonth}
          onLeftBound={() => dayField.current?.focus()}
          label={placeholders.month}
          name="month"
          required={required}
          maxLength={2}
          isError={isError.includes('MM')}
          value={value.MM}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, MM: event.target.value })
          }
          onBlur={handleBlurMonth}
          onFocus={handleFocus}
          ref={monthField}
          className={styles.datePickerField}
          dataTestIdPrefix={toKebabCase(dataTestIdPrefix, 'month')}
        />
        <ChainableNumberField
          onLeftBound={() => monthField.current?.focus()}
          label={placeholders.year}
          name="year"
          required={required}
          maxLength={4}
          isError={isError.includes('YYYY')}
          value={value.YYYY}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, YYYY: event.target.value })
          }
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={yearField}
          className={styles.datePickerField}
          dataTestIdPrefix={toKebabCase(dataTestIdPrefix, 'year')}
        />
      </div>
    </InputGroup>
  );
};

/**
 * Adds leading zeroes to the day and month fields. This is consciously
 * separated from the validation. Because this should be applied in different
 * situations.
 */
const padField = (n: string) => (n === '' ? '' : n.padStart(2, '0'));

export default DatePicker;
